import { EventPageType, PageKind } from '@graphql/generated-base';
import type { PageContentType } from '@graphql/generated-base';
import type { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';

const MIGRATED_CUSTOM_PAGE_SLUGS_LIST: ReadonlyArray<MigratedCustomPageSlug> = ['story', 'faq', 'registrylinks', 'travel', 'vip', 'welcome', 'schedule'];
export const MIGRATED_CUSTOM_PAGE_SLUGS: ReadonlySet<MigratedCustomPageSlug> = new Set(MIGRATED_CUSTOM_PAGE_SLUGS_LIST);

// Mapping from migrated page slugs to their corresponding legacy EventPageType.
// Note: 'registrylinks' slug maps to EventPageType.registry — legacy name mismatch.
const SLUG_TO_LEGACY_PAGE_TYPE: Readonly<Partial<Record<MigratedCustomPageSlug, EventPageType>>> = {
  story: EventPageType.story,
  faq: EventPageType.faq,
  registrylinks: EventPageType.registry,
  travel: EventPageType.travel,
  vip: EventPageType.vip,
  welcome: EventPageType.welcome,
  schedule: EventPageType.schedule,
};

export const getLegacyPageType = (slug: MigratedCustomPageSlug): EventPageType | undefined => SLUG_TO_LEGACY_PAGE_TYPE[slug];

// Mapping from EventPageType to canonical slug for migrated pages.
// e.g., EventPageType.vip → 'vip' (canonical even if the pageSlug differs).
const PAGE_TYPE_TO_SLUG: Readonly<Partial<Record<EventPageType, MigratedCustomPageSlug>>> = {
  [EventPageType.story]: 'story',
  [EventPageType.faq]: 'faq',
  [EventPageType.registry]: 'registrylinks',
  [EventPageType.travel]: 'travel',
  [EventPageType.vip]: 'vip',
  [EventPageType.welcome]: 'welcome',
  [EventPageType.schedule]: 'schedule',
};

export const getMigratedPageSlug = (pageType: EventPageType): MigratedCustomPageSlug | undefined => PAGE_TYPE_TO_SLUG[pageType];

// True for the legacy registry page (`EventPageType.registry`) and its migrated form
// (`EventPageType.custom` with a slug that canonicalizes to 'registrylinks').
export const isRegistryPage = (pageType: EventPageType | null | undefined, pageSlug: string | null | undefined): boolean => {
  if (pageType === EventPageType.registry) return true;
  return pageType === EventPageType.custom && toCanonicalMigratedSlug(pageSlug) === 'registrylinks';
};

/** The slug event_service gives the new-stack RSVP page container (MGT-93). */
export const RSVP_PAGE_SLUG = 'rsvp';

// Legacy RSVP page or the new-stack `custom` container at slug 'rsvp'. Not a migrated slug on
// purpose: adding it there would change routing and admin editors for legacy events too.
/** WARNING: a `custom` page at 'rsvp' is the RSVP page only on a new-stack event — callers must gate on `useIsNewStackEvent()` (MGT-176). */
export const isRsvpPage = (pageType: EventPageType | null | undefined, pageSlug: string | null | undefined): boolean => {
  if (pageType === EventPageType.rsvp) return true;
  return pageType === EventPageType.custom && pageSlug?.toLowerCase() === RSVP_PAGE_SLUG;
};

// Canonicalizes a data-slug or EventPageType value to its MigratedCustomPageSlug.
// Needed because migrated registrylinks carries pageSlug='registry' in data while
// the canonical (flag-keyed / URL) slug is 'registrylinks'.
export const toCanonicalMigratedSlug = (pageSlug: string | undefined | null): MigratedCustomPageSlug | undefined => {
  if (!pageSlug) return undefined;
  const lower = pageSlug.toLowerCase();
  if (MIGRATED_CUSTOM_PAGE_SLUGS.has(lower as MigratedCustomPageSlug)) {
    return lower as MigratedCustomPageSlug;
  }
  return PAGE_TYPE_TO_SLUG[lower as EventPageType];
};

// True when a slug names one of the seven system-migrated pages, in either its canonical (URL)
// or its stored (data) form — e.g. both 'registrylinks' and 'registry'. False for ordinary
// custom pages, which is what keeps them id-keyed and on the /page/<slug> URL.
export const isMigratedCustomPageSlug = (pageSlug: string | undefined | null): boolean => !!toCanonicalMigratedSlug(pageSlug);

// Legacy story or system-migrated custom story (not userCustom squatters); used to suppress the stale page note.
export const isLegacyOrMigratedStoryPage = (pageType: EventPageType | null | undefined, pageSlug: string | null | undefined, pageKind: PageKind | null | undefined): boolean =>
  pageType === EventPageType.story || (pageType === EventPageType.custom && pageKind === PageKind.systemCustom && toCanonicalMigratedSlug(pageSlug) === 'story');

type PageWithTypeAndSlug = Readonly<{
  pageSlug: string;
  type?: EventPageType | null;
  // CORE-6470: page-kind decisions now branch on `pageKind` (systemLegacy | systemCustom |
  // userCustom) rather than `type` + `contentType`. `type`/`contentType` remain on the type
  // transitionally for non-kind callers, but are no longer read for kind classification.
  pageKind?: PageKind | null;
  contentType?: PageContentType | null;
}>;

// CORE-6456: a user-created custom page squatting a built-in/migrated slug
// (e.g. a page the user named "Schedule"). It must route/render as a plain
// custom page, not as the migrated/native page.
// CORE-6470: identified by pageKind === userCustom (was type === custom + contentType === userGenerated).
export const isUserGeneratedMigratedSquatter = (page: PageWithTypeAndSlug): boolean => page.pageKind === PageKind.userCustom && !!toCanonicalMigratedSlug(page.pageSlug);

// These custom pages always use native routing/entry even though they're EventPageType.custom.
const ALWAYS_NATIVE_PAGE_SLUGS: ReadonlySet<string> = new Set(['accommodations']);

/**
 * Determines if a page should use the native/dictionary entry (with correct icon/path)
 * instead of the generic custom page handler.
 */
type PageContainerInfo =
  | Readonly<{
      pageKind?: PageKind | null;
      contentType?: PageContentType | null;
    }>
  | null
  | undefined;

/**
 * Checks whether an event has a system-created (migrated/internal) custom page for the given slug.
 * CORE-6470: expressed via `pageKind` instead of the raw `type` + `contentType` matrix, but
 * behavior-preserving — true iff BOTH:
 *   1. a custom page record exists for the slug (`pageKind !== systemLegacy`, i.e. the old
 *      `type === custom` check — note the original did NOT constrain the record's own contentType), and
 *   2. the corresponding PageContainer is system-created (`pageKind === systemCustom`, i.e. the
 *      old `contentType === default` check).
 * Kept as an AND (not "either side is systemCustom"): a `default` container with no matching page
 * record — or a custom page record whose container is not `default` — must NOT count as migrated,
 * otherwise the admin would flip from the legacy editor to the custom editor.
 *
 * This check is data-driven (not flag-driven) so the custom page editor stays active
 * even if the feature flag is later toggled off after migration.
 *
 * The slug is canonicalized so callers can pass either the URL/canonical form
 * (e.g., 'registrylinks') or the underlying container/data form (e.g., 'registry').
 */
export const hasMigratedCustomPage = (pages: ReadonlyArray<PageWithTypeAndSlug> | undefined, slug: string, pageContainer: PageContainerInfo): boolean => {
  const targetCanonical = toCanonicalMigratedSlug(slug);
  if (!targetCanonical) return false;
  const hasCustomPageRecord = pages?.some(p => p.pageKind !== PageKind.systemLegacy && toCanonicalMigratedSlug(p.pageSlug) === targetCanonical) ?? false;
  const containerIsSystemCustom = pageContainer?.pageKind === PageKind.systemCustom;
  return hasCustomPageRecord && containerIsSystemCustom;
};

type ContestableContainer = Readonly<{
  slug: string;
  pageKind?: PageKind | null;
}>;

type ContestingPage = Readonly<{
  pageSlug: string;
  pageKind?: PageKind | null;
}>;

const slugsMatch = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** The event's page row for a slug, in list order — ambiguous when two rows share the slug. */
export const findCustomPageBySlug = <T extends ContestingPage>(pages: ReadonlyArray<T> | null | undefined, pageSlug: string): T | undefined =>
  pages?.find(page => slugsMatch(page.pageSlug, pageSlug));

/**
 * PRNT-4817: the page meant by the `/{eventHandle}/page/{slug}` URL.
 *
 * Since the migration a `systemCustom` archive and the couple's `userCustom` page can share one
 * slug. The archive keeps the canonical `/{eventHandle}/{slug}` URL, so the `page/` URL is only
 * ever reached for the couple's page — while plain list order returns whichever row the event
 * happens to carry first (in practice the system row). Falls back to that row when the slug is
 * uncontested, which is every non-migrated custom page.
 *
 * Only for callers that already know the URL names one specific page. On the canonical route the
 * server's route-ownership rules must keep deciding — see `useCustomData`.
 */
export const selectRoutedCustomPage = <T extends ContestingPage>(pages: ReadonlyArray<T> | null | undefined, pageSlug: string): T | undefined =>
  pages?.find(page => page.pageKind === PageKind.userCustom && slugsMatch(page.pageSlug, pageSlug)) ?? findCustomPageBySlug(pages, pageSlug);

const isContestedSlug = (pages: ReadonlyArray<ContestingPage> | null | undefined, pageSlug: string): boolean => {
  let hasUserCustom = false;
  let hasOther = false;
  for (const page of pages ?? []) {
    if (!slugsMatch(page.pageSlug, pageSlug)) continue;
    if (page.pageKind === PageKind.userCustom) hasUserCustom = true;
    else hasOther = true;
  }
  return hasUserCustom && hasOther;
};

/**
 * The `pageKind` a GuestPageContainerBySlug fetch should send. Only a contested slug on its
 * `/page/<slug>` URL needs one; anywhere else it would only split the Apollo cache key when the
 * URL changes on scroll, unmounting the page while the duplicate fetch loads. Guests never see a
 * hidden archive in `pages`, so for them the slug is contested only when both containers are visible.
 */
export const selectRoutedPageKind = (pages: ReadonlyArray<ContestingPage> | null | undefined, pageSlug: string, isRoutedAtThisPage: boolean): PageKind | undefined =>
  isRoutedAtThisPage && isContestedSlug(pages, pageSlug) ? PageKind.userCustom : undefined;

/**
 * PRNT-4591: a migration-created `systemCustom` container and the couple's own `userCustom`
 * container can share a single slug (e.g. two `travel` containers). `pageContainerBySlug`
 * deliberately resolves the `systemCustom` row, so the admin editor would open the archived
 * migration page instead of the live page guests actually see.
 *
 * True when the resolved (system) container's slug is also occupied by a userCustom page.
 * Callers use it to decide whether the fuller container list is worth fetching — for the
 * overwhelming majority of events it is not.
 */
export const hasContestedUserGeneratedPage = (resolvedContainer: ContestableContainer | null | undefined, pages: ReadonlyArray<ContestingPage> | null | undefined): boolean => {
  if (resolvedContainer?.pageKind !== PageKind.systemCustom) return false;
  return pages?.some(page => page.pageKind === PageKind.userCustom && slugsMatch(page.pageSlug, resolvedContainer.slug)) ?? false;
};

/**
 * Picks the container whose *content* the admin editor should read and write.
 *
 * Deliberately separate from `hasMigratedCustomPage`: that helper must keep reading the resolved
 * `systemCustom` container, because it is the signal that keeps the new custom-page editor
 * mounted — if it read the userCustom container it would return false and the admin would fall
 * back to the legacy editor. So the resolved container stays the migration *signal* and this
 * helper answers the separate question of which container holds the page being edited.
 *
 * Returns the resolved container untouched unless it is `systemCustom` and a `userCustom`
 * container shares its slug, in which case the userCustom (live, couple-owned) container wins.
 */
export const selectEditableCustomPageContainer = <T extends ContestableContainer>(
  resolvedContainer: T | null | undefined,
  containers: ReadonlyArray<T> | null | undefined,
): T | null | undefined => {
  if (resolvedContainer?.pageKind !== PageKind.systemCustom) return resolvedContainer;
  return containers?.find(container => container.pageKind === PageKind.userCustom && slugsMatch(container.slug, resolvedContainer.slug)) ?? resolvedContainer;
};

// Returns the PageContainer `slug` used by the backend for a given URL/canonical slug.
// Most migrations store the container under the same slug as the URL segment
// (e.g. 'travel' → 'travel'); registrylinks stores it under 'registry'
// (EventPageType.registry) for backend replacement matching.
// Safe to call with any slug — returns the input unchanged when no translation applies.
//
// CORE-6456: a userCustom page squatting a migrated slug is unique by its literal
// `(event, slug)` and must be fetched by that exact slug — canonicalizing it would
// resolve the wrong (system/native) container. Pass `pageKind` so squatters skip the
// translation.
export const toPageContainerSlug = (pageSlug: string | undefined | null, pageKind?: PageKind | null): string => {
  if (!pageSlug) return '';
  if (isUserGeneratedMigratedSquatter({ pageSlug, pageKind })) return pageSlug;
  const canonical = toCanonicalMigratedSlug(pageSlug);
  const legacyType = canonical ? SLUG_TO_LEGACY_PAGE_TYPE[canonical] : undefined;
  return (legacyType as string | undefined) ?? pageSlug;
};

// Every migrated page type now has a systemCustom counterpart, so a legacy native row is dropped
// whenever its migrated row is present. Squatters never trigger the drop: a page the couple merely
// named "Schedule" is an ordinary custom page (CORE-6456).
export const filterMigratedLegacyPages = <T extends PageWithTypeAndSlug>(pages: ReadonlyArray<T>): ReadonlyArray<T> => {
  const legacyTypesToFilter = new Set<EventPageType>();

  pages.forEach(page => {
    if (page.pageKind === PageKind.systemLegacy) return;
    if (isUserGeneratedMigratedSquatter(page)) return;
    const canonical = toCanonicalMigratedSlug(page.pageSlug);
    if (!canonical) return;
    const legacyType = SLUG_TO_LEGACY_PAGE_TYPE[canonical];
    if (!legacyType) return;
    legacyTypesToFilter.add(legacyType);
  });

  if (legacyTypesToFilter.size === 0) return pages;

  // Only systemLegacy rows are ever dropped; custom rows are always kept.
  return pages.filter(page => page.pageKind !== PageKind.systemLegacy || !legacyTypesToFilter.has(page.type as EventPageType));
};

export const shouldUseNativePageEntry = (page: PageWithTypeAndSlug): boolean => {
  // CORE-6456: a userGenerated page squatting a migrated slug is a plain custom page —
  // it must be id-keyed, never routed through the native dictionary entry.
  if (isUserGeneratedMigratedSquatter(page)) return false;

  // Always use native entry for specific slugs (e.g., accommodations)
  if (ALWAYS_NATIVE_PAGE_SLUGS.has(page.pageSlug)) return true;

  // CORE-6470: systemLegacy (built-in) pages always use native entry.
  if (page.pageKind === PageKind.systemLegacy) return true;

  // Migrated pages keep the native dictionary entry (correct icon/path); ordinary custom pages
  // have no canonical slug and are id-keyed instead.
  return !!toCanonicalMigratedSlug(page.pageSlug);
};
